import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import cx from 'classnames';
import useBreakpoint from 'utils/hooks/useBreakpoint';
import breakpoints from 'utils/breakpoints';
import HeartIcon from 'components/icons/Heart';
import HeartFilled from 'components/icons/HeartFilled';
import useFavorites from 'utils/hooks/useFavorites';
import { Button } from 'components/components/atoms';
import useMeasure from 'utils/hooks/useMeasure';
import { hasPrice } from 'utils/parsePrice';

import { colors } from 'utils/tailwindColors';

const Container = styled.div`
  z-index: 20;
  width: 12rem;
  right: 5rem;
  ${({ cardHeight }) => cardHeight && `bottom: ${cardHeight}px;`}
`;

const IconContainer = styled.div`
  width: 2.25rem;
  height: 2.25rem;
`;

const Content = styled.div`
  transition: all 0.4s ease-out;
  height: auto;
`;

const ImageContainer = styled.div`
  height: 7.5rem;
`;

const StyledButton = styled(Button)`
  height: 3.5rem;
  background-color: ${colors['action-blue']};
  border-radius: 0rem;
`;

const CardTitle = styled.div`
  letter-spacing: -0.029rem;
`;

const CardSubtitle = styled.div`
  letter-spacing: 0.007rem;
`;

const AvailableFlag = styled.div`
  border-radius: 0.813rem;
  width: fit-content;
`;

const getCommunityStylesFormat = (communityStyles) => {
  const styles = communityStyles.split(',');
  if (styles && styles.length > 1) {
    let newFormat = styles.reduce(function (a, b) {
      return a.concat(', ' + b);
    });
    return newFormat;
  }

  return communityStyles;
};

const MapSearchCard = ({ card = {}, ...rest }) => {
  const favoritesList = rest.favoritesList;
  const breakpoint = useBreakpoint(
    typeof window !== 'undefined' && window.innerWidth,
  );
  const isMobile = ['sm', 'xs'].includes(breakpoint);

  const { saveFavorite, deleteFavorite } = useFavorites();
  const [cardRefHeight, { height }] = useMeasure();
  const [cardHeight, setCardHeight] = useState(300);

  const id = card.id;
  const statusShortDescription = card.statusShortDescription;
  const titleCard = card.title || '';
  const communityStyles = card.communityStyles;
  const homeType = card.homeType || '';
  const price = card.price;
  const hasDisplayPrice = price && hasPrice(price.price);
  const ctaAction = card.ctaAction;
  const isFavorite = favoritesList.includes(id);
  const image = card.image;

  const handleFavoriteClick = (id) => {
    if (!isFavorite) {
      saveFavorite(id);
    } else {
      deleteFavorite(id);
    }
  };
  useEffect(() => {
    setCardHeight(height);
    window.addEventListener('resize', setCardHeight(height));
    return window.removeEventListener('resize', setCardHeight(height));
  }, [height]);

  return (
    <Container
      className={
        'relative w-full flex flex-col justify-between overflow-hidden rounded transform'
      }
      cardHeight={cardHeight + 42}
      id={'InfoWindow-' + id}
      ref={cardRefHeight}
    >
      <Content className='md:cursor-pointer z-20 bg-white' isMobile={isMobile}>
        <ImageContainer>
          <a
            href={card.url}
            className='h-full w-full relative'
            aria-label={'View ' + titleCard + ' information'}
          >
            {image && image.src && (
              <img
                className='object-cover h-full min-w-full'
                src={card.image.src}
                alt={card.image.alt}
              />
            )}
          </a>
          <IconContainer
            className='flex items-center absolute top-0 right-0 justify-center
                rounded-full bg-white text-action-blue mt-10-mattamy mr-3 hover:bg-mattamy-blue
                hover:text-white md:cursor-pointer'
            onClick={() => handleFavoriteClick(id)}
            role='button'
            aria-label={isFavorite ? 'Mark as unfavorite' : 'Mark as favorite'}
            aria-pressed={isFavorite ? 'true' : 'false'}
            tabIndex={0}
          >
            {isFavorite ? (
              <HeartFilled className='h-5 w-5' />
            ) : (
              <HeartIcon className='h-5 w-5' />
            )}
          </IconContainer>
        </ImageContainer>
        <a
          href={card.url}
          aria-label={'View ' + titleCard + 'information'}
          className='mx-5 mt-2 flex flex-col w-auto items-left text-left md:items-center md:text-center '
        >
          {statusShortDescription && (
            <AvailableFlag
              className='flex items-center justify-center
                  px-6 mt-10-mattamy md:mt-0 bg-white text-mattamy-blue-light
                  hover:bg-mattamy-blue-light hover:text-white border-2 border-mattamy-blue-light'
            >
              <p className='text-center font-trade-gothic-20 uppercase text-xs tracking-wide leading-3 mt-1'>
                {statusShortDescription}
              </p>
            </AvailableFlag>
          )}

          <CardTitle className='font-trade-gothic-20 text-mattamy-blue text-2xl mt-2'>
            {titleCard}
          </CardTitle>

          {communityStyles && (
            <p className='text-mattamy-gray-font text-md font-trade-gothic-20 text-left font-bold mt-1'>
              {getCommunityStylesFormat(communityStyles)}
            </p>
          )}

          {homeType && (
            <span className='font-graphie text-action-blue text-xs mt-1'>
              {homeType}
            </span>
          )}

          {price && (hasDisplayPrice || price.noPriceText) && (
            <div className='block flex items-center flex-col my-1 pb-3'>
              {hasDisplayPrice ? (
                <>
                  <CardSubtitle className='font-graphie text-mattamy-blue-light text-sm leading-5'>
                    {price.normalText}
                  </CardSubtitle>
                  <CardTitle className='font-trade-gothic-20 text-mattamy-blue text-xl leading-9 md:-mb-1 ml-3 md:ml-0'>
                    {price.price}
                  </CardTitle>
                </>
              ) : (
                <CardTitle className='font-trade-gothic-20 text-mattamy-blue text-xl leading-9 md:-mb-1 ml-0 pb-5'>
                  {price.noPriceText}
                </CardTitle>
              )}
            </div>
          )}
        </a>
      </Content>
      {ctaAction && ctaAction.text && ctaAction.href && (
        <StyledButton
          color={'action-blue'}
          variant='filled'
          className='block text-sm text-white font-trade-gothic-20 pt-0 pb-0 md:w-full hover:bg-mattamy-blue hover:text-white'
          href={ctaAction.href}
          target={ctaAction.target}
          aria-label={ctaAction.text}
          onClick={() => {
            if (
              ctaAction.text.toLowerCase() ===
              'Join interest list'.toLowerCase()
            ) {
              window.dataLayer.push({ event: 'join-list' });
            }
            if (
              ctaAction.text.toLowerCase() ===
              'Register for Updates'.toLowerCase()
            ) {
              window.dataLayer.push({ event: 'register' });
            }
          }}
        >
          {ctaAction.text}
        </StyledButton>
      )}
    </Container>
  );
};

MapSearchCard.propTypes = {
  card: PropTypes.object,
};

export default MapSearchCard;
